<template>
    <v-dialog
        v-model="dialog"
        fullscreen
    >
        <v-card>
            <v-toolbar
                dark
                color="primary"
            >
                <v-toolbar-title>{{dialog_title}}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                    <v-btn
                        dark
                        text
                        @click="onClickDiscardChanges"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        dark
                        text
                        :disabled="!is_valid"
                        @click="onClickSave"
                    >
                        Save &amp; Close
                    </v-btn>
                </v-toolbar-items>
            </v-toolbar>

            <v-card-text>

                <v-form
                    autocomplete="off"
                >
                    <v-container>
                        <v-card-title>
                            Recipient Phone Number
                        </v-card-title>

                        <m-autocomplete-phone-number
                            label="Contacts"
                            autofocus
                            :organization_id="bulk_message_recipient.organization_id"
                            v-on:input="setEndpointValue"
                            :rules="[rules.required]"
                            :set_from_endpoint_value="bulk_message_recipient.recipient_endpoint"
                            ref="autocomplete-phone-number"
                        />

                        <v-card-title>
                            Extra Columns
                            <v-icon
                                right
                                style="pointer-events: auto;"
                                v-on:click="showExtraColumnInfo"
                            >
                                mdi-information-outline
                            </v-icon>
                        </v-card-title>

                        <v-row
                            v-for="(value, name, index) in bulk_message_recipient.extra_fields"
                            v-bind:key="index"
                            no-gutters
                        >
                            <v-col
                                cols="12"
                                sm="6"
                                style="padding-right: 15px"
                            >
                                <m-edit-text
                                    label="Column Name"
                                    :value="name"
                                    :index="index"
                                    v-on:input="updateColumnName"
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <m-edit-text
                                    label="Column Value"
                                    :value="value"
                                    :index="index"
                                    v-on:input="updateColumnValue"
                                />
                            </v-col>
                        </v-row>

                        <m-icon-bar
                            v-on:add="onClickCreate"
                        />

                    </v-container>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>

const { v4: uuidv4 } = require('uuid');

export default {

    data() {
        return {
            dialog: false,
            bulk_message_recipient: {},

            rules: {
                required: (value) => { if ( value == null || value.trim().length == 0 ) return 'Required'; return true;  },
            },
        }
    },

    methods: {

        showExtraColumnInfo()
        {
            this.$rc.alert(
                'Extra Columns',
                'Any Extra Columns can be added to help keep track of leads, such as Date Entered, or Type of Product requested.'
            );
        },

        showDialog(organization_id, bulk_message_id, bulk_message_recipient)
        {
            if ( !bulk_message_recipient )
            {
                this.bulk_message_recipient = {
                    'organization_id' : organization_id,
                    'bulk_message_id' : bulk_message_id,
                    'bulk_message_recipient_id' : uuidv4(),
                    'recipient_endpoint' : null,
                    'extra_fields' : {},
                    'has_opted_out_of_bulk_text' : false,
                    'shortened_link_click_count': 0,
                };

                // fix for weird bug where editing a contact, then creating a new one doesn't properly clear out the
                // autcomplete phone dialog
                if ( this.$refs['autocomplete-phone-number'] )
                {
                    this.$refs['autocomplete-phone-number'].clearInternalValue();
                }
            }
            else
            {
                this.bulk_message_recipient = bulk_message_recipient;
            }

            this.dialog = true;
            this.$router.dialog_guard = this.dialogGuard;

            this.populateInitialExtraFields(bulk_message_id);
        },

        populateInitialExtraFields(bulk_message_id)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/bulk/recipients/search',
            
                'params' : {
                        'bulk_message_id': bulk_message_id,
                        'offset': 0,
                        'limit': 1, // don't need more than one result, we are only interested in recipient_extra_fields
                    },
            
                'on_success': (data) => {
                        
                        let results = data.recipient_extra_fields;

                        for( let idx=0; idx < results.length; idx++ )
                        {
                            if ( this.bulk_message_recipient.extra_fields[results[idx].extra_field_key] === undefined )
                            {
                                this.bulk_message_recipient.extra_fields[results[idx].extra_field_key] = '';
                            }
                        }

                        this.$forceUpdate();

                    },
            
                'on_error': 'Unable to list extra fields',
            });
        },

        setEndpointValue(new_endpoint)
        {
            if ( new_endpoint )
            {
                this.bulk_message_recipient.recipient_endpoint = new_endpoint.value;
            }
        },

        closeDialog()
        {
            this.dialog = false;
            this.$router.dialog_guard = null;
        },

        
        dialogGuard(to, from, next)
        {
            if ( this.dialog == false)
            {
                // slipup somehow
                this.$router.dialog_guard = null;
                return;
            }

            // The dialog is showing...
            if ( this.is_valid )
            {
                // data is valid, so save
                this.save();
                this.dialog = false;
                next();
                return;
            }

            this.$rc.snackbar('You must either correct the errors or discard your changes.',3500);
            this.dialog = true;
            next(false);
        },

        onClickSave()
        {
            this.save();
            this.closeDialog();
        },

        onClickDiscardChanges()
        {
            this.closeDialog();
        },

        onClickCreate()
        {
            let idx = Object.keys(this.bulk_message_recipient.extra_fields).length + 1;

            let new_column_name = "New Column " + idx;
            this.bulk_message_recipient.extra_fields[new_column_name] = 'Value';

            this.$forceUpdate();
        },

        updateColumnName(new_name, index)
        {
            let old_key = Object.keys(this.bulk_message_recipient.extra_fields)[index];
            let value = this.bulk_message_recipient.extra_fields[old_key];

            delete this.bulk_message_recipient.extra_fields[old_key];

            // now reinsert the column with value
            this.bulk_message_recipient.extra_fields[new_name] = value;

            this.$forceUpdate();
        },

        updateColumnValue(new_value, index)
        {
            let key = Object.keys(this.bulk_message_recipient.extra_fields)[index];
            this.bulk_message_recipient.extra_fields[key] = new_value;

            this.$forceUpdate();
        },

        save()
        {
            this.$rc.showSpinner();

            this.$rc.putBulkMessageRecipient(
                this.bulk_message_recipient,
                (data) => {
                    this.$rc.hideSpinner();
                    this.closeDialog();

                    this.$rc.snackbar('Recipient created', 3000);
                    this.$emit('ok', data);
                },
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to complete API call');
                },
            );
        },
    },
    computed: {
        is_valid() {
            return this.bulk_message_recipient != null
                && this.bulk_message_recipient.recipient_endpoint != null && this.bulk_message_recipient.recipient_endpoint != '';
        },

        dialog_title() {

            if ( this.bulk_message_recipient )
            {
                return "Edit Bulk Message Recipient";
            }

            return "Create Bulk Message Recipient";
        },
    },
}
</script>

<style lang="scss" scoped>
</style>